/* eslint-disable react/no-unknown-property */
import {
  FontWeight, HStack, Message, Popover, PopoverProps, Stack, Text,
} from '@doveit/bricks';
import React from 'react';
import { formatEuro, formatPercentage } from '@doveit/hammer';
import { Invoice } from '../../../providers/api/dtos';
import * as styles from './InvoiceValuePopover.style';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { useInvoiceCommissionsByRole } from '../../hooks/use-invoice-commissions-by-role/useInvoiceCommissionsByRole';

export interface InvoiceValuePopoverProps extends React.AriaAttributes {
  invoice: Invoice,
  trigger: PopoverProps['trigger'],
  hideCommissions?: boolean,
}

const InvoiceValuePopover: React.FC<InvoiceValuePopoverProps> = ({
  invoice,
  trigger,
  hideCommissions = false,
  ...rest
}) => {
  const { data: filteredCommissions = [], authorized: filteredCommissionsAuthorized } = useInvoiceCommissionsByRole(invoice);

  const displayPaidValue = React.useMemo(
    () => typeof invoice.paidValue !== 'undefined' && invoice.paidValue !== invoice.value,
    [invoice.paidValue, invoice.value],
  );

  return (
    <Popover
      trigger={trigger}
      hasArrow
      {...rest}
    >
      <div css={styles.wrapper}>
        <Stack gap={150}>
          {invoice.vat !== undefined
            && (
              <>
                <Text.BodySmall fontWeight={FontWeight.MEDIUM}>
                  Importo
                </Text.BodySmall>

                <Stack>
                  <HStack
                    alignItems="center"
                    justifyContent="space-between"
                    gap={100}
                  >
                    <Text.BodySmall fontWeight={FontWeight.REGULAR}>
                      Netto
                    </Text.BodySmall>
                    <hr css={styles.dashedLine} />

                    <HStack>
                      <Text.BodySmall
                        fontWeight={FontWeight.REGULAR}
                        color={displayPaidValue ? 'critical.default.low' : 'neutral.default.low'}
                      >
                        {formatEuro(invoice.value)}
                      </Text.BodySmall>
                      <Text.Mini fontWeight={FontWeight.LIGHT}>
                        (+{formatPercentage(invoice.vat)} IVA)
                      </Text.Mini>
                    </HStack>
                  </HStack>

                  {displayPaidValue && (
                    <HStack
                      alignItems="center"
                      justifyContent="space-between"
                      gap={100}
                    >
                      <Text.BodySmall fontWeight={FontWeight.REGULAR}>
                        Effettivo
                      </Text.BodySmall>
                      <hr css={styles.dashedLine} />

                      <HStack>
                        <Text.BodySmall fontWeight={FontWeight.REGULAR}>
                          {formatEuro(invoice.paidValue!)}
                        </Text.BodySmall>
                        <Text.Mini fontWeight={FontWeight.LIGHT}>
                          (+{formatPercentage(invoice.vat)} IVA)
                        </Text.Mini>
                      </HStack>
                    </HStack>
                  )}

                </Stack>
              </>
            )}

          {!hideCommissions && filteredCommissionsAuthorized && (
            <>
              <hr css={styles.solidDivider} />

              {filteredCommissions.length === 0 && (
                <Message message="Non sono presenti commissioni" />
              )}

              {filteredCommissions.length > 0 && (
                <>
                  <Text.BodySmall fontWeight={FontWeight.MEDIUM}>
                    Commissioni
                  </Text.BodySmall>

                  {filteredCommissions.map((commission) => (
                    <HStack
                      key={commission.agentId}
                      alignItems="center"
                      justifyContent="space-between"
                      wrap="nowrap"
                      gap={100}
                    >
                      <div css={styles.agentWrapper}>
                        <ViewAgentSmallPreview agentIdOrEmail={commission.agentId} size="S" />
                      </div>

                      <div css={styles.dashedLine} />

                      <HStack wrap="nowrap">
                        <Text.BodySmall
                          as="strong"
                          fontWeight={FontWeight.REGULAR}
                          whitespace="nowrap"
                        >
                          {formatEuro(commission.amount)}
                        </Text.BodySmall>
                        <Text.Mini fontWeight={FontWeight.LIGHT}>
                          ({formatPercentage(commission.percentage)})
                        </Text.Mini>
                      </HStack>
                    </HStack>
                  ))}
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </Popover>
  );
};

export default InvoiceValuePopover;
